type ResponsiveInput = {
  base?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
};

function responsive({
  base = '', sm, md, lg, xl,
}: ResponsiveInput): string {
  return [base, sm, md, lg, xl].filter(Boolean).join(' ');
}

const defaultStyles = {
  layout: {
    page: 'w-full m-auto flex flex-col overflow-hidden bg-gradient-to-b from-[#f7f7f7] to-white',
    header: [
      'mx-auto flex w-full max-w-2xl shrink-0 justify-center',
      'bg-white px-4 pt-6',
    ].join(' '),
    pageHeader: 'mx-auto max-w-3xl px-4 pt-4 text-center',
    contentArea: 'populate-check-in-body flex-1 overflow-y-auto overflow-x-hidden px-4 pt-0 pb-6',
    flowContainer: responsive({
      base: 'mx-auto flex min-h-full w-full max-w-7xl flex-1 justify-center pt-0',
      sm: 'sm:pt-0',
    }),
    shell: 'mx-auto flex h-full min-h-full w-full max-w-2xl flex-1 flex-col gap-0',
    progressHeader: [
      'sticky top-0 z-[5] flex shrink-0 flex-col gap-6',
      'border-b border-gray-100 bg-white pb-4',
    ].join(' '),
    cardWrapper: 'flex min-h-0 flex-1 flex-col',
    card: 'relative flex w-full flex-1 flex-col overflow-hidden bg-white',
    cardInner: 'flex min-h-0 flex-1 flex-col',
    cardPadding: responsive({
      base: 'p-6',
      sm: 'sm:p-8',
      lg: 'lg:p-10',
    }),
    contentScroll: 'flex flex-1 flex-col overflow-y-auto pb-28 md:pb-32',
    stepIconContainer: [
      'flex h-9 w-9 shrink-0 items-center justify-center',
      'rounded-full bg-populate-green/10',
    ].join(' '),
    footer: [
      'fixed bottom-0 left-1/2 z-10 w-full max-w-2xl -translate-x-1/2',
      'border-t border-gray-100 bg-white py-4 md:py-5',
      'pb-[max(1rem,env(safe-area-inset-bottom))]',
    ].join(' '),
    footerInner: responsive({
      base: [
        'mx-auto flex w-full max-w-2xl gap-4 px-4',
        'md:flex-row md:items-stretch md:gap-5',
      ].join(' '),
      sm: 'sm:px-8',
      lg: 'lg:px-10',
    }),
    statusMessage: 'mx-auto max-w-2xl rounded-2xl border border-gray-200 bg-white px-6 py-8 text-center shadow-sm',
    statusMessageTitle: 'text-xl font-bold xl:text-lg',
    statusMessageBody: 'mt-3 text-base text-gray-600 xl:text-sm',
    statusMessageButton: 'mt-6 text-base font-semibold text-populate-green xl:text-sm',
    errorText: 'text-sm text-red-500 xl:text-xs',
    errorButton: 'text-base font-semibold text-populate-green xl:text-sm',
    loadingContainer: 'flex min-h-[50vh] items-center justify-center',
    errorContainer: 'mx-auto max-w-2xl space-y-3 text-center',
  },
  type: {
    eyebrow: responsive({
      base: 'text-center text-xs font-semibold uppercase tracking-widest text-gray-400 pt-4',
      sm: 'sm:text-sm',
      xl: 'xl:text-xs',
    }),
    pageTitle: responsive({
      base: 'mt-1 text-xl font-bold text-gray-700',
      md: 'md:text-2xl',
      xl: 'xl:text-xl',
    }),
    title: responsive({
      base: 'text-xl font-semibold leading-tight text-gray-900',
      sm: 'sm:text-2xl',
      md: 'md:text-3xl',
      xl: 'xl:text-2xl',
    }),
    lead: responsive({
      base: 'text-sm leading-snug text-gray-700',
      md: 'md:text-base',
      xl: 'xl:text-sm',
    }),
    body: responsive({
      base: 'text-sm leading-snug text-gray-700',
      lg: 'lg:text-base',
      xl: 'xl:text-sm',
    }),
    label: responsive({
      base: 'text-sm font-semibold text-gray-700',
      md: 'md:text-lg',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    description: responsive({
      base: 'text-sm text-gray-600',
      md: 'md:text-base',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    featureText: responsive({
      base: 'text-sm font-medium leading-snug text-gray-600',
      md: 'md:text-lg',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    cardTitle: responsive({
      base: 'text-sm font-semibold text-gray-900',
      md: 'md:text-base',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    cardDescription: responsive({
      base: 'text-sm text-gray-700',
      md: 'md:text-base',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    callout: responsive({
      base: 'text-sm leading-relaxed text-gray-900',
      lg: 'lg:text-base',
      xl: 'xl:text-sm',
    }),
    button: 'text-base font-semibold xl:text-sm',
  },
  space: {
    stepIntro: responsive({
      base: 'flex items-start gap-4',
      lg: 'lg:gap-5',
    }),
    headerStack: responsive({
      base: 'flex flex-col gap-2',
      lg: 'lg:gap-3',
    }),
    leadOffset: responsive({
      base: 'mt-2',
      md: 'md:mt-3',
      lg: 'lg:mt-4',
    }),
    sectionsOffset: responsive({
      base: 'mt-3',
      md: 'md:mt-6',
    }),
    sectionStack: responsive({
      base: 'flex flex-col gap-4',
      lg: 'lg:gap-5',
    }),
    listStack: responsive({
      base: 'flex flex-col gap-3',
      md: 'md:gap-4',
    }),
    cardStack: responsive({
      base: 'flex flex-col gap-3',
      lg: 'lg:gap-4',
    }),
    featureItem: 'flex items-start gap-3',
    cardItemHeader: 'flex items-center gap-2',
    cardDescriptionOffset: 'mt-1 ml-8',
    infoRow: 'flex items-start gap-3',
    infoRowContent: 'flex flex-1 flex-col pt-0.5',
    cardItem: responsive({
      base: 'rounded-xl border p-4',
      lg: 'lg:p-5',
    }),
    callout: responsive({
      base: 'rounded-xl border border-populate-green/15 bg-populate-green/10 p-3.5',
      md: 'md:text-lg',
      lg: 'lg:p-4',
    }),
  },
  icon: {
    step: '!h-5 !w-5 !text-populate-green',
    tileBase: 'flex items-center justify-center bg-populate-green/10',
    tileColor: '!text-populate-green',
    tileSm: {
      container: 'h-10 min-h-10 w-10 min-w-10 rounded-lg',
      icon: '!h-5 !w-5',
    },
    tileLg: {
      container: 'h-12 min-h-12 w-12 min-w-12 rounded-xl',
      icon: '!text-[24px]',
    },
    tone: {
      brand: '!text-populate-green',
      warning: '!text-yellow-500',
      danger: '!text-red-500',
      neutral: '!text-gray-600',
    },
    cardToneIcon: responsive({
      base: '!h-6 !w-6',
    }),
    primaryArrow: '!text-base',
    secondaryArrow: '!text-base',
  },
  cardTone: {
    brand: 'border-populate-green/15 bg-populate-green/10',
    danger: 'border-red-500/15 bg-red-500/10',
    warning: 'border-yellow-500/15 bg-yellow-500/10',
    neutral: 'border-gray-200 bg-gray-50',
  },
  stepper: {
    nav: 'flex w-full items-center justify-center',
    list: 'mx-auto flex w-full max-w-[500px] items-center',
    circle: 'flex h-8 w-8 items-center justify-center rounded-full text-lg font-medium xl:text-base',
    checkIcon: '!text-[16px]',
    connector: 'h-0.5 flex-1',
    listItem: 'flex items-center',
    listItemWithConnector: 'flex-1',
  },
  actions: {
    primary: [
      'inline-flex w-full flex-1 items-center justify-center gap-2 rounded-xl',
      'bg-populate-green px-5 py-3 text-base font-semibold text-white transition',
      'hover:bg-populate-deep-green disabled:cursor-not-allowed disabled:opacity-60',
      'md:min-h-11 md:px-6 xl:text-sm',
    ].join(' '),
    secondary: [
      'inline-flex w-full flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3',
      'text-base font-semibold text-gray-600 transition hover:border-gray-300 hover:text-gray-900',
      'disabled:cursor-not-allowed disabled:border-gray-200 disabled:opacity-40',
      'md:min-h-11 md:px-6 xl:text-sm',
    ].join(' '),
  },
} as const;

type RtmEnrollmentStyles = typeof defaultStyles;

function createVariantStyles(overrides: {
  layout?: Partial<RtmEnrollmentStyles['layout']>;
  type?: Partial<RtmEnrollmentStyles['type']>;
  space?: Partial<RtmEnrollmentStyles['space']>;
}): RtmEnrollmentStyles {
  return {
    ...defaultStyles,
    layout: { ...defaultStyles.layout, ...overrides.layout },
    type: { ...defaultStyles.type, ...overrides.type },
    space: { ...defaultStyles.space, ...overrides.space },
  };
}

const compactStyles = createVariantStyles({
  layout: {
    cardPadding: responsive({
      base: 'p-5',
      sm: 'sm:p-6',
      lg: 'lg:p-8',
    }),
  },
  type: {
    title: responsive({
      base: 'text-lg font-semibold leading-tight text-gray-900',
      sm: 'sm:text-xl',
      lg: 'lg:text-2xl',
      xl: 'xl:text-xl',
    }),
    lead: responsive({
      base: 'text-sm leading-relaxed text-gray-700',
      lg: 'lg:text-base',
      xl: 'xl:text-sm',
    }),
    body: responsive({
      base: 'text-sm leading-relaxed text-gray-700',
      lg: 'lg:text-base',
      xl: 'xl:text-sm',
    }),
    label: responsive({
      base: 'text-xs font-semibold text-gray-900',
      lg: 'lg:text-sm',
      xl: 'xl:text-xs',
    }),
    description: responsive({
      base: 'text-xs text-gray-600',
      lg: 'lg:text-sm',
      xl: 'xl:text-xs',
    }),
    featureText: responsive({
      base: 'text-xs font-medium text-gray-600',
      lg: 'lg:text-sm',
      xl: 'xl:text-xs',
    }),
    cardTitle: responsive({
      base: 'text-xs font-semibold text-gray-900',
      lg: 'lg:text-sm',
      xl: 'xl:text-xs',
    }),
    cardDescription: responsive({
      base: 'text-xs text-gray-700',
      lg: 'lg:text-sm',
      xl: 'xl:text-xs',
    }),
    callout: responsive({
      base: 'text-sm leading-relaxed text-gray-900',
      lg: 'lg:text-base',
      xl: 'xl:text-sm',
    }),
  },
  space: {
    headerStack: responsive({
      base: 'flex flex-col gap-1.5',
      lg: 'lg:gap-2',
    }),
    sectionsOffset: responsive({
      base: 'mt-3',
      lg: 'lg:mt-4',
    }),
    sectionStack: responsive({
      base: 'flex flex-col gap-4',
      lg: 'lg:gap-5',
    }),
    listStack: responsive({
      base: 'flex flex-col gap-3',
      lg: 'lg:gap-4',
    }),
    cardStack: responsive({
      base: 'flex flex-col gap-2',
      lg: 'lg:gap-3',
    }),
    cardItem: responsive({
      base: 'rounded-xl border p-3',
      lg: 'lg:p-4',
    }),
    callout: responsive({
      base: 'rounded-xl border border-populate-green/15 bg-populate-green/10 p-3',
      lg: 'lg:p-4',
    }),
  },
});

const spaciousStyles = createVariantStyles({
  layout: {
    cardPadding: responsive({
      base: 'p-8',
      sm: 'sm:p-10',
      lg: 'lg:p-12',
    }),
  },
  type: {
    title: responsive({
      base: 'text-2xl font-semibold leading-tight text-gray-900',
      sm: 'sm:text-3xl',
      lg: 'lg:text-4xl',
      xl: 'xl:text-3xl',
    }),
    lead: responsive({
      base: 'text-lg leading-relaxed text-gray-700',
      lg: 'lg:text-xl',
      xl: 'xl:text-lg',
    }),
    body: responsive({
      base: 'text-lg leading-relaxed text-gray-700',
      lg: 'lg:text-xl',
      xl: 'xl:text-lg',
    }),
    label: responsive({
      base: 'text-base font-semibold text-gray-900',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    description: responsive({
      base: 'text-base text-gray-600',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    featureText: responsive({
      base: 'text-base font-medium text-gray-600',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    cardTitle: responsive({
      base: 'text-base font-semibold text-gray-900',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    cardDescription: responsive({
      base: 'text-base text-gray-700',
      lg: 'lg:text-lg',
      xl: 'xl:text-base',
    }),
    callout: responsive({
      base: 'text-lg leading-relaxed text-gray-900',
      lg: 'lg:text-xl',
      xl: 'xl:text-lg',
    }),
  },
  space: {
    headerStack: responsive({
      base: 'flex flex-col gap-3',
      lg: 'lg:gap-4',
    }),
    leadOffset: responsive({
      base: 'mt-2',
      lg: 'lg:mt-3',
    }),
    sectionsOffset: responsive({
      base: 'mt-6',
      lg: 'lg:mt-8',
    }),
    sectionStack: responsive({
      base: 'flex flex-col gap-6',
      lg: 'lg:gap-8',
    }),
    listStack: responsive({
      base: 'flex flex-col gap-5',
      lg: 'lg:gap-6',
    }),
    cardStack: responsive({
      base: 'flex flex-col gap-4',
      lg: 'lg:gap-5',
    }),
    cardItem: responsive({
      base: 'rounded-xl border p-5',
      lg: 'lg:p-6',
    }),
    callout: responsive({
      base: 'rounded-xl border border-populate-green/15 bg-populate-green/10 p-5',
      lg: 'lg:p-6',
    }),
  },
});

export const RTM_ENROLLMENT_VARIANT = 'default' as const;

const rtmEnrollmentVariants = {
  default: defaultStyles,
  compact: compactStyles,
  spacious: spaciousStyles,
} as const;

export type RtmEnrollmentVariant = keyof typeof rtmEnrollmentVariants;

export function getRtmEnrollmentStyles(
  variant: RtmEnrollmentVariant = RTM_ENROLLMENT_VARIANT,
): RtmEnrollmentStyles {
  return rtmEnrollmentVariants[variant];
}
